import { ProductOut, StockDetails } from '../../../../store/api';
import React, { FC, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ProductCover from '../../../../shared/common/ProductCover';
import MaterialIcon from '../../../../shared/common/MaterialIcon';
import { ExtendedManageSubscriptionTableOverviewCell } from '../../utils/subscriptionTableTileTypes';
import { IssueUsageStatisticsCell } from '../../../../shared/subscription-management/IssueUsageStatisticsCell';
import { IssueUsage } from '../../../../shared/subscription-management/types/IssueUsage';
import classNames from 'classnames';
import { ProductCopyStockDropdown } from '../../../../shared/product/ProductCopyStock';
import { Trans, useTranslation } from 'react-i18next';
import { LOANFORMAT_EPRESS_OBJECT } from '../../../../shared/loanFormat/constants';

export type ExtendedOverviewSubscriptionUsageLimitValue = {
    type: ExtendedManageSubscriptionTableOverviewCell.UsageLimitValue;
    overviewLabel: string;
    individuellLabel: string;
    sharedLabel: string;
    stockDetails: StockDetails;
};

export type ExtendedOverviewSubscriptionUsageLimitNone = {
    type: ExtendedManageSubscriptionTableOverviewCell.UsageLimitNone;
    label: string;
};

export type ExtendedOverviewSubscriptionExpired = {
    type: ExtendedManageSubscriptionTableOverviewCell.Expired;
    expirationDate: string | undefined;
};
export type ExtendedOverviewSubscriptionCeased = {
    type: ExtendedManageSubscriptionTableOverviewCell.Ceased;
    expirationDate: string | undefined;
};

export type ExtendedManageSubscriptionOverview = {
    product: ProductOut;
    userSubscribedLabel?: string;
    showWarning?: 'orange' | 'red';
    currentYearSubscription:
        | ExtendedOverviewSubscriptionUsageLimitValue
        | ExtendedOverviewSubscriptionUsageLimitNone
        | ExtendedOverviewSubscriptionExpired
        | ExtendedOverviewSubscriptionCeased;
    nextYearSubscription: ExtendedOverviewSubscriptionUsageLimitValue | ExtendedOverviewSubscriptionUsageLimitNone;
    previousIssueUsage?: IssueUsage;
    currentIssueUsage?: IssueUsage;
    expired: boolean;
    ceased: boolean;
};

export type ExtendedManageSubscriptionOverviewProps = ExtendedManageSubscriptionOverview & {
    copiesListOpen: boolean;
    toggleCopiesList: () => void;
};

export const ExtendedManageSubscriptionOverviewRow: FC<ExtendedManageSubscriptionOverviewProps> = props => {
    const { t } = useTranslation();
    const {
        product,
        copiesListOpen,
        toggleCopiesList,
        currentYearSubscription,
        nextYearSubscription,
        previousIssueUsage,
        currentIssueUsage,
        userSubscribedLabel,
        showWarning,
        expired,
        ceased
    } = props;

    return (
        <tr className="overview-row">
            <td className={classNames('product-cover-cell', { expired: expired || ceased })}>
                <Link to={`/products/${product.id}`}>
                    <ProductCover product={product} minSizeInPx={157} />
                </Link>
            </td>
            <td className="product-title-cell">
                <div>
                    <div>
                        <Link to={`/products/${product.id}`} className={classNames({ expired: expired || ceased })}>
                            <b>{product.title}</b>
                        </Link>
                        {expired ? <MaterialIcon icon="event_busy" className="red" /> : null}
                        {ceased ? <MaterialIcon icon="close" className="red" /> : null}
                        {showWarning ? <MaterialIcon icon="warning" className={showWarning} /> : null}
                    </div>
                    <small>{userSubscribedLabel ? userSubscribedLabel : null}</small>
                </div>
                <button onClick={toggleCopiesList}>
                    {t('shop.subscriptions.showCopies')}
                    <MaterialIcon icon={copiesListOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} />
                </button>
            </td>
            <td className="current-year-subscriptions">
                {(() => {
                    switch (currentYearSubscription.type) {
                        case ExtendedManageSubscriptionTableOverviewCell.UsageLimitValue:
                            return <ExtendedOverviewSubscriptionUsageLimitValueView {...currentYearSubscription} />;
                        case ExtendedManageSubscriptionTableOverviewCell.UsageLimitNone:
                            return (
                                <>
                                    <small>{currentYearSubscription.label}</small>
                                    <div>
                                        (0 + <MaterialIcon icon="shared" primary={false} className="shared-icon" />
                                        0)
                                    </div>
                                </>
                            );
                        case ExtendedManageSubscriptionTableOverviewCell.Expired:
                            return (
                                <>
                                    <div>
                                        <Trans
                                            i18nKey="shop.subscriptions.expired"
                                            components={{ boldRed: <b className="red" />, small: <small />, b: <b /> }}
                                            values={{
                                                date: t('dateFormat.dateWithMonth', {
                                                    val: new Date(currentYearSubscription.expirationDate ?? '')
                                                })
                                            }}
                                        />
                                    </div>
                                    <div>
                                        (0 + <MaterialIcon icon="shared" primary={false} className="shared-icon" />
                                        0)
                                    </div>
                                </>
                            );

                        case ExtendedManageSubscriptionTableOverviewCell.Ceased:
                            return (
                                <>
                                    <div>
                                        <Trans
                                            i18nKey="shop.subscriptions.ceased"
                                            components={{ boldRed: <b className="red" />, small: <small />, b: <b /> }}
                                            values={{
                                                date: t('dateFormat.dateWithMonth', {
                                                    val: new Date(currentYearSubscription.expirationDate ?? '')
                                                })
                                            }}
                                        />
                                    </div>
                                    <div>
                                        (0 + <MaterialIcon icon="shared" primary={false} className="shared-icon" />
                                        0)
                                    </div>
                                </>
                            );
                        default:
                            return <></>;
                    }
                })()}
            </td>
            <td className="previous-issue-usage">
                {previousIssueUsage ? <IssueUsageStatisticsCell {...previousIssueUsage} /> : null}
            </td>
            <td className="current-issue-usage">
                {currentIssueUsage ? <IssueUsageStatisticsCell {...currentIssueUsage} /> : null}
            </td>
            <td className="next-year-subscriptions">
                {(() => {
                    switch (nextYearSubscription.type) {
                        case ExtendedManageSubscriptionTableOverviewCell.UsageLimitValue:
                            return <ExtendedOverviewSubscriptionUsageLimitValueView {...nextYearSubscription} />;
                        case ExtendedManageSubscriptionTableOverviewCell.UsageLimitNone:
                            return <small>{nextYearSubscription.label}</small>;
                        default:
                            return <></>;
                    }
                })()}
            </td>
        </tr>
    );
};

const ExtendedOverviewSubscriptionUsageLimitValueView: FC<ExtendedOverviewSubscriptionUsageLimitValue> = props => {
    const ref = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);

    return (
        <>
            <div ref={ref} onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}>
                <b>{props.overviewLabel}</b>
                <div>
                    ({props.individuellLabel} + <MaterialIcon icon="shared" primary={false} className="shared-icon" />
                    {props.sharedLabel})
                </div>
            </div>
            <ProductCopyStockDropdown
                loanFormat={LOANFORMAT_EPRESS_OBJECT}
                stockDetails={props.stockDetails}
                placement="top-start"
                visible={visible}
                popoverRef={ref}
            />
        </>
    );
};
